'use client';

import React, { useEffect, useState } from 'react';

import Button from '@/components/Button';
import Image from '@/components/NextImage';

import { useLanguage } from '@/contexts/LanguageContext';
import { redirectToWhatsApp } from '@/utils/whatsapp';

type SlideKey = 'rhinoplasty' | 'liposuction' | 'bbl';

const slides: { 
  key: SlideKey; 
  image: string;
  gradient: string;
}[] = [
  {
    key: 'rhinoplasty',
    image: "/images/burun.webp",
    gradient: "from-blue-500 to-purple-600"
  },
  {
    key: 'liposuction',
    image: "/images/liposuction.webp",
    gradient: "from-green-500 to-blue-600"
  },
  {
    key: 'bbl',
    image: "/images/brezilya-poposu-estetigi.webp",
    gradient: "from-pink-500 to-purple-600"
  }
];

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t, locale, localeUrls } = useLanguage();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-0">
      <div className="relative">
        <div className="relative h-[520px] sm:h-[600px] overflow-hidden rounded-3xl">
          {/* Mobile Background - Lightweight gradient instead of image */}
          <div className="absolute inset-0 md:hidden z-0">
            <div className={`w-full h-full bg-gradient-to-br ${slides[currentSlide].gradient} opacity-20`}></div>
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 to-white/60"></div>
          </div>
          
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${currentSlide === index
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 translate-x-full'
                }`}
            >
              <div className="flex flex-col md:flex-row items-center gap-8 sm:gap-12 h-full relative z-10">
                <div className="flex-1 w-full p-6 sm:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Op. Dr. Barış Çin</span>
                  </div>
                  

                  
                  <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#181C32] mb-4 sm:mb-6 leading-tight whitespace-pre-line">
                    {t.homepage.hero.slides[slide.key].title}
                  </h2>
                  <p className="text-base sm:text-lg text-[#181C32]/80 mb-6 sm:mb-8 max-w-xl">
                    {t.homepage.hero.slides[slide.key].description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 mb-6 sm:mb-8">
                    <Button
                      variant="primary"
                      className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      onClick={() => redirectToWhatsApp(locale)}
                      aria-label={t.homepage.hero.button}
                    >
                      {t.homepage.hero.button}
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg border border-blue-600 text-blue-600 hover:bg-blue-50"
                      onClick={() => {
                        const getSlideUrl = (key: SlideKey) => {
                          switch (key) {
                            case 'rhinoplasty':
                              return localeUrls.burunEstetigi;
                            case 'liposuction':
                              return localeUrls.liposuction;
                            case 'bbl':
                              return localeUrls.brezilyaPoposuEstetigi;
                            default:
                              return localeUrls.burunEstetigi;
                          }
                        };
                        window.location.href = getSlideUrl(slide.key);
                      }}
                      aria-label={t.homepage.hero.slides[slide.key].infoButton}
                    >
                      {t.homepage.hero.slides[slide.key].infoButton}
                    </Button>
                  </div>
                </div>
                
                {/* Desktop Image - Only load on desktop */}
                <div className="flex-1 hidden md:flex justify-center items-center w-full h-full">
                  <Image
                    src={slide.image}
                    alt={t.homepage.hero.slides[slide.key].title}
                    width={800}
                    height={1000}
                    className="object-cover w-full h-full"
                    priority={index === 0}
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Dots */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-2" role="tablist" aria-label="Slide navigation">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index
                ? 'bg-blue-600 w-8'
                : 'bg-blue-300 hover:bg-blue-400'
                }`}
              role="tab"
              aria-selected={currentSlide === index}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-all duration-300"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-all duration-300"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
} 